import { Button } from "@fluentui/react-components";
import { PersonEdit24Regular } from "@fluentui/react-icons";
import { PeopleAddIcon } from "@fluentui/react-icons-mdl2";
import { AppSpinner } from "components/AppSpinner/AppSpinner";
import { ActionButton, Flex, StyledTooltip } from "components/styled";
import { useAppTheme } from "hooks/useAppTheme";
import { useAtomValue } from "jotai";
import { EmployeeRowItem } from "library/types";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetDependentDetails } from "state/queries/useGetDependentDetails";
import { useGetEmployeeDetails } from "state/queries/useGetEmployeeDetails";
import { selectedCompanyState } from "store/UIHrPage";
import { EmployeeFormDialog } from "./EmployeeFormDialog";

type Props = {
  disabled?: boolean;
  item?: EmployeeRowItem;

  isAddEmployee?: boolean;
  showManagePackageEligibilityOption?: boolean;
};

export function ManageEmployee({
  isAddEmployee,
  disabled,
  item,
  showManagePackageEligibilityOption = true,
}: Props) {
  const { palette } = useAppTheme();
  const { t } = useTranslation();
  const selectedCompany = useAtomValue(selectedCompanyState);
  const [employeeDetailsOpen, setEmployeeDetailsOpen] = useState(false);

  const { data: employeeData, isLoading: employeeLoading } =
    useGetEmployeeDetails({
      countryCode: Number(item?.countryCode),
      clientInternalNumberGOS: Number(item?.clientInternalNumberGos),
      employeeEmail: item?.employeeLoginEmailAddress || "",
      enabled: employeeDetailsOpen && !isAddEmployee && !item?.isDependent,
    });

  const { data: dependentData, isLoading: dependentLoading } =
    useGetDependentDetails({
      countryCode: Number(item?.countryCode),
      clientInternalNumberGOS: Number(item?.clientInternalNumberGos),
      employeeLoginEmailAddress: item?.employeeLoginEmailAddress || "",
      employeeDependentTag: Number(item?.employeeDependentTag),
      enabled: employeeDetailsOpen && !isAddEmployee && item?.isDependent,
    });

  const handleDialogStateChange = useCallback(() => {
    setEmployeeDetailsOpen((prev) => !prev);
  }, []);

  const packageUserData = item?.isDependent ? dependentData : employeeData;

  const hasNeedAttentionFields =
    item?.needsAttentionInformation?.toLowerCase().replace("new", "") ||
    item?.documentRecentlyAdded;

  const isLoading = employeeLoading || dependentLoading;

  return (
    <>
      <Flex width={"100%"} justify="center">
        {isAddEmployee ? (
          <ActionButton
            data-help="hr-add-employee"
            noBorder
            disabled={disabled}
            appearance="subtle"
            icon={<PeopleAddIcon />}
            onClick={() => {
              setEmployeeDetailsOpen(true);
            }}
          >
            {t("hbh.addEmployee.label")}
          </ActionButton>
        ) : (
          <StyledTooltip
            relationship="label"
            content={{
              children: t("hbh.edit.label"),
            }}
          >
            <Button
              data-help="hr-employee-edit"
              icon={
                <PersonEdit24Regular
                  style={{
                    ...(disabled ||
                    (!showManagePackageEligibilityOption &&
                      !hasNeedAttentionFields)
                      ? {}
                      : { color: palette.purpleDark }),
                    ...(hasNeedAttentionFields
                      ? { color: palette.yellowDark }
                      : {}),
                    transform: "scale(1.1)",
                  }}
                />
              }
              disabled={
                disabled ||
                isLoading ||
                (!showManagePackageEligibilityOption && !hasNeedAttentionFields)
              }
              appearance="transparent"
              size="small"
              onClick={() => {
                setEmployeeDetailsOpen(true);
              }}
            />
          </StyledTooltip>
        )}
      </Flex>
      {employeeDetailsOpen && (
        <EmployeeFormDialog
          item={(isAddEmployee ? item : packageUserData) as EmployeeRowItem}
          employeeDocumentRecentlyAdded={item?.documentRecentlyAdded}
          open={employeeDetailsOpen}
          setOpen={handleDialogStateChange}
          isAddEmployee={isAddEmployee}
          selectedCompany={{
            clientInternalNumberGos: item
              ? item?.clientInternalNumberGos
              : selectedCompany?.clientInternalNumberGos,
            countryCode: item
              ? item?.countryCode
              : selectedCompany?.countryCode,
          }}
        />
      )}
    </>
  );
}
